/**
 * Server-side SEO metadata registry.
 *
 * Single source of truth for the <title>, <meta name="description">,
 * canonical, og:* and JSON-LD that TanStack Start emits into the *server*
 * HTML for every public route. Crawlers that don't run JS (GPTBot,
 * ClaudeBot, PerplexityBot, and Googlebot's first pass) read this.
 *
 * The underlying entries live in scripts/prerenderRoutes.ts, which was the
 * pre-migration prerender registry — it is reused verbatim so titles and
 * descriptions stay identical to what Search Console already has indexed.
 */
import { prerenderRoutes, type RouteMeta } from "../../../scripts/prerenderRoutes";
import {
  EXPLAINER_DESCRIPTION,
  EXPLAINER_JSON_LD,
  EXPLAINER_TITLE,
} from "@/data/howWeMakeMoneyData";
import { REAL_AFFORDABILITY_JSON_LD } from "@/data/realAffordabilityData";
import { MORTGAGE_READY_JSON_LD } from "@/data/mortgageReadyChecklistData";
import { CASH_TO_CLOSE_JSON_LD } from "@/data/cashToCloseData";
import {
  LOAN_ESTIMATE_COMPARE_JSON_LD,
  PAGE_DESCRIPTION as LOAN_ESTIMATE_COMPARE_DESCRIPTION,
  PAGE_TITLE as LOAN_ESTIMATE_COMPARE_TITLE,
} from "@/data/loanEstimateCompareData";
import { HOME_VALUE_HUB_JSON_LD } from "@/data/homeValueHubData";
import {
  HOUSING_COUNSELOR_JSON_LD,
  PAGE_DESCRIPTION as HOUSING_COUNSELOR_DESCRIPTION,
  PAGE_TITLE as HOUSING_COUNSELOR_TITLE,
} from "@/data/housingCounselorData";
import {
  NONPROFIT_MORTGAGE_JSON_LD,
  PAGE_DESCRIPTION as NONPROFIT_MORTGAGE_DESCRIPTION,
  PAGE_TITLE as NONPROFIT_MORTGAGE_TITLE,
} from "@/data/nonprofitMortgageHelpData";
import { NACA_DESCRIPTION, NACA_JSON_LD, NACA_TITLE } from "@/data/nacaPartnerData";
import {
  PARTNERS,
  PARTNERS_HUB_DESCRIPTION,
  PARTNERS_HUB_JSON_LD,
  PARTNERS_HUB_PATH,
  PARTNERS_HUB_TITLE,
  buildPartnerJsonLd,
} from "@/data/partners";

import {
  COMBINING_ASSISTANCE_JSON_LD,
  PAGE_DESCRIPTION as COMBINING_ASSISTANCE_DESCRIPTION,
  PAGE_TITLE as COMBINING_ASSISTANCE_TITLE,
} from "@/data/combiningAssistanceData";
import {
  FREE_READINESS_TOOLS_JSON_LD,
  PAGE_DESCRIPTION as FREE_READINESS_TOOLS_DESCRIPTION,
  PAGE_TITLE as FREE_READINESS_TOOLS_TITLE,
} from "@/data/freeReadinessToolsData";
import {
  DPA_HIDDEN_STRINGS_JSON_LD,
  PAGE_DESCRIPTION as DPA_HIDDEN_STRINGS_DESCRIPTION,
  PAGE_TITLE as DPA_HIDDEN_STRINGS_TITLE,
} from "@/data/dpaHiddenStringsData";
import {
  ASSISTANCE_FUNDING_JSON_LD,
  PAGE_DESCRIPTION as ASSISTANCE_FUNDING_DESCRIPTION,
  PAGE_TITLE as ASSISTANCE_FUNDING_TITLE,
} from "@/data/assistanceFundingData";
import {
  CLOSING_DISCLOSURE_JSON_LD,
  PAGE_DESCRIPTION as CLOSING_DISCLOSURE_DESCRIPTION,
  PAGE_TITLE as CLOSING_DISCLOSURE_TITLE,
} from "@/data/closingDisclosureData";
import {
  MORTGAGE_PREAPPROVAL_JSON_LD,
  PAGE_DESCRIPTION as MORTGAGE_PREAPPROVAL_DESCRIPTION,
  PAGE_TITLE as MORTGAGE_PREAPPROVAL_TITLE,
} from "@/data/mortgagePreapprovalData";
import {
  FIRST_RESPONDER_JSON_LD,
  PAGE_DESCRIPTION as FIRST_RESPONDER_DESCRIPTION,
  PAGE_TITLE as FIRST_RESPONDER_TITLE,
} from "@/data/firstResponderData";
import { AVOID_FORECLOSURE_JSON_LD } from "@/data/avoidForeclosureSchema";
import { RAISE_CREDIT_SCORE_BEFORE_MORTGAGE_JSON_LD } from "@/data/raiseCreditScoreBeforeMortgageSchema";
import { LOW_INCOME_FIRST_TIME_BUYER_GRANTS_JSON_LD } from "@/data/lowIncomeFirstTimeBuyerGrantsSchema";
import { RELOCATION_TIMING_JSON_LD } from "@/data/relocationTimingSchema";
import { COMPARE_LENDER_QUOTES_JSON_LD } from "@/data/compareLenderQuotesSchema";
import { HIGH_INCOME_DOWN_PAYMENT_HELP_JSON_LD } from "@/data/highIncomeDownPaymentHelpSchema";
import { DPA_SELL_OR_REFINANCE_JSON_LD } from "@/data/dpaSellOrRefinanceSchema";
import { LOAN_OFFICER_QUESTIONS_JSON_LD } from "@/data/loanOfficerQuestionsSchema";
import { HOMEBUYER_READINESS_SCORE_JSON_LD } from "@/data/homebuyerReadinessScoreSchema";
import { HOME_BUYING_TIMELINE_JSON_LD } from "@/data/homeBuyingTimelineSchema";

export const SITE_URL = "https://www.homeapproach.com";
export const SITE_NAME = "Home Approach";

export interface ResolvedMeta {
  title: string;
  description: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogType: string;
  image?: string;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  noIndex?: boolean;
}

const REGISTRY = new Map<string, RouteMeta>(
  prerenderRoutes.map((r) => [normalizePath(r.path), r]),
);

/**
 * Routes that exist in the app but weren't in the legacy prerender list.
 * Public ones get real meta; nothing private is listed here (private paths
 * fall through to PRIVATE_PATTERNS below and are emitted noindex).
 */
const EXTRA: RouteMeta[] = [
  {
    path: "/how-we-make-money",
    title: EXPLAINER_TITLE,
    description: EXPLAINER_DESCRIPTION,
    jsonLd: EXPLAINER_JSON_LD,
  },
  {
    path: "/stack",
    title: "Grant Stack — See Every Dollar You Qualify For",
    description:
      "Stack federal, state, county, city, and employer down payment help into one total. Free, 60 seconds, no credit pull required.",
  },
  {
    path: "/stack/guide",
    title: "How Grant Stacking Works — Home Approach Stack Guide",
    description:
      "How down payment assistance layers combine, which programs can be stacked, repayment triggers to watch, and how to claim each one.",
  },
  {
    path: "/closing-costs",
    title: "Closing Cost Estimator — What Buyers Actually Pay",
    description:
      "Estimate your buyer closing costs by state and price point: lender fees, title, transfer tax, prepaids, and how to negotiate them down.",
  },
  {
    path: "/mortgage-rates",
    title: "Mortgage Rates Today — National & State Averages 2026",
    description:
      "Today's 30-year and 15-year mortgage rate averages, state-by-state comparisons, and payment math on your loan amount. Updated daily.",
  },
  {
    path: "/grants",
    title: "Down Payment Assistance by State — 2026 Grant Directory",
    description:
      "Browse first-time homebuyer grants and down payment assistance programs in all 50 states and DC. See eligibility and stack your total.",
  },
  {
    path: "/first-time-buyer",
    title: "First-Time Home Buyer Programs by State — 2026 Guide",
    description:
      "Every first-time buyer program by state and metro: eligibility, credit score minimums, and how to stack them with FHA, VA, and USDA loans.",
  },
  {
    path: "/relocation-calculator",
    title: "Relocation Cost Calculator — DIY vs. Pro Movers",
    description:
      "Estimate the true cost of your move: movers, truck rental, deposits, storage, and cost-of-living change between two cities.",
  },
  {
    path: "/homebuying-support",
    title: "Homebuying Support — Free Help at Every Step",
    description:
      "Free homebuying support: grant finding, credit prep, lender matching, and an AI guide that answers your questions in plain English.",
  },
  {
    path: "/homeowner-checkup",
    title: "Homeowner Checkup — Equity, Refi & Insurance Snapshot",
    description:
      "Get a free 60-second checkup on your home's equity, refinance opportunity, insurance fit, and tax efficiency. Built for current homeowners.",
  },
  {
    path: "/tools/compare-lender-quotes",
    title: "Compare Lender Quotes — Side-by-Side Loan Estimate Tool",
    description:
      "Compare up to three lender quotes side by side: rate, points, fees, monthly payment, and break-even. See which offer actually costs less.",
  
    jsonLd: COMPARE_LENDER_QUOTES_JSON_LD,},
  {
    path: "/tools/repayment-triggers",
    title: "DPA Repayment Triggers — When You Have to Pay It Back",
    description:
      "Down payment assistance isn't always free money. See which events trigger repayment, how forgiveness clocks work, and how to stay compliant.",
  },
  {
    path: "/tools/avoid-foreclosure",
    title: "How to Avoid Foreclosure — Step-by-Step Action Plan",
    description:
      "Behind on your mortgage? Work through forbearance, loan modification, and HUD counseling options with a clear timeline and checklist.",
  
    jsonLd: AVOID_FORECLOSURE_JSON_LD,},
  {
    path: "/tools/student-loan-mortgage-guide",
    title: "Do Student Loans Stop You From Qualifying for a Mortgage?",
    description:
      "See exactly how Conventional, FHA, VA, and USDA loans treat student loan payments in your DTI, worked examples, 2026 repayment changes, and a free DTI estimator.",
  },
  {
    path: "/tools/dpa-sell-or-refinance",
    title: "DPA if You Sell or Refinance Early — What You Owe",
    description:
      "Selling or refinancing with down payment assistance? See payoff timing by DPA type, unforgiven balance math, subordination rules, and recapture tax.",
  
    jsonLd: DPA_SELL_OR_REFINANCE_JSON_LD,},
  {
    path: "/tools/buy-before-selling",
    title: "How to Buy a New Home Before Selling Your Current One",
    description:
      "Bridge loan vs HELOC, 401(k) loan, sale contingency, rent-back, extended closing, or keep it as a rental — compare every buy-before-you-sell option, with a decision matrix and 30-60-90 day timeline.",
  },
  {
    path: "/tools/inherited-home-mortgage",
    title: "Inherited a Home With a Mortgage? Your Options Explained",
    description:
      "What are my options if I inherit a home with a mortgage? Compare keeping and paying, selling, disclaiming, and reverse mortgage payoff — plus successor-in-interest steps, a lender checklist, and multiple-heir guidance.",
  },
  {
    path: "/tools/loan-officer-questions",
    title: "Questions to Ask a Mortgage Loan Officer — 2026 Checklist",
    description:
      "Questions to ask a loan officer before you commit: loan programs, Loan Estimate fees, rate locks, underwriting and communication. Not a quote or approval.",
  
    jsonLd: LOAN_OFFICER_QUESTIONS_JSON_LD,},
  {
    path: "/mortgage-glossary",
    title: "Mortgage Glossary — Plain-English Definitions",
    description:
      "Plain-English definitions for every mortgage and homebuying term: APR, PMI, escrow, DTI, points, and 100+ more.",
  },
  {
    path: "/tools/raise-credit-score-before-mortgage",
    title: "How to Raise Your Credit Score Fast Before a Mortgage",
    description:
      "A 30/60/90-day plan for the scores lenders actually pull: mortgage FICO 2, 4, and 5, AZEO and statement timing, rapid rescoring, disputes, collections, and what backfires.",
  
    jsonLd: RAISE_CREDIT_SCORE_BEFORE_MORTGAGE_JSON_LD,},
  {
    path: "/tools/high-income-down-payment-help",
    title: "Down Payment Help for High-Income Buyers — 2026 Guide",
    description:
      "Earn too much for standard DPA? Compare no-income-limit options, census tract grants, high-limit HFA programs, physician loans, and low-down-payment alternatives.",
  
    jsonLd: HIGH_INCOME_DOWN_PAYMENT_HELP_JSON_LD,},
  {
    path: "/tools/homebuyer-readiness-score",
    title: "What Is a Homebuyer Readiness Score? Factors + Free Calculator",
    description:
      "A homebuyer readiness score combines credit, debt-to-income, cash, financial history, and employment into one number. See the five factors, a sample weighted formula, score bands, worked examples, and calculate your own estimate.",
  
    jsonLd: HOMEBUYER_READINESS_SCORE_JSON_LD,},
  {
    path: "/tools/home-buying-timeline",
    title: "Home Buying Timeline: Every Step From Start to Closing Day",
    description:
      "A realistic list of every step from pre-approval to closing day \u2014 20 chronological milestones, how long each phase takes, common delays, and a closing week checklist.",
  
    jsonLd: HOME_BUYING_TIMELINE_JSON_LD,},
  {
    path: "/tools/low-income-first-time-buyer-grants",
    title: "Grants for Low-Income First-Time Home Buyers (2026)",
    description:
      "Which programs are true grants and which are forgivable or deferred loans: FHLBank set-asides, HUD vouchers, USDA 502 Direct, HOME funds, NHF, Chenoa, NACA, Habitat, and bank grants.",
  
    jsonLd: LOW_INCOME_FIRST_TIME_BUYER_GRANTS_JSON_LD,},
  {
    path: "/tools/relocation-timing",
    title: "How to Time Selling Your Home and Buying in Another City",
    description:
      "Sell first, buy first, or close the same day \u2014 compare the three timing paths for a cross-city move, plus bridge loans, HELOCs, recasting, rent-backs, and a dated relocation checklist.",
  
    jsonLd: RELOCATION_TIMING_JSON_LD,},
  {
    path: "/tools/real-affordability",
    title: "What Home Payment Can I Actually Afford? Net-Income Calculator",
    description:
      "Lenders qualify you on gross income. A livable budget starts with net. See the 25% comfort target, the 30% ceiling, the ghost expenses DTI ignores, and calculate your real monthly housing number.",
    jsonLd: REAL_AFFORDABILITY_JSON_LD,
  },
  {
    path: "/tools/mortgage-ready-checklist",
    title: "Mortgage-Ready Checklist: Every Box to Tick Before You Apply",
    description:
      "A 36% DTI target, a 620+ conventional baseline, 2\u20135% closing costs, cash reserves, and the documents and gift letters underwriting asks for \u2014 an interactive, printable mortgage-ready checklist.",
    jsonLd: MORTGAGE_READY_JSON_LD,
  },
  {
    path: "/tools/cash-to-close",
    title: "What Is Cash to Close and Why Does It Keep Changing?",
    description:
      "Cash to close is not the same as closing costs. See the formula, a full line-item breakdown, why the number moves between the Loan Estimate and Closing Disclosure, and how to verify it before you wire.",
    jsonLd: CASH_TO_CLOSE_JSON_LD,
  },
  {
    path: "/tools/what-is-my-home-worth",
    title: "What Is My Home Worth Right Now?",
    description:
      "Three ways to find out what your home is actually worth: an instant online estimate, a free agent CMA, and a licensed appraisal. Compare speed, accuracy, and cost, then get a value range for your address.",
    jsonLd: HOME_VALUE_HUB_JSON_LD,
  },
  {
    path: "/tools/find-housing-counselor",
    title: HOUSING_COUNSELOR_TITLE,
    description: HOUSING_COUNSELOR_DESCRIPTION,
    jsonLd: HOUSING_COUNSELOR_JSON_LD,
  },
  {
    path: "/partners/naca",
    title: NACA_TITLE,
    description: NACA_DESCRIPTION,
    jsonLd: NACA_JSON_LD,
  },
  {
    path: "/tools/nonprofit-mortgage-help",
    title: NONPROFIT_MORTGAGE_TITLE,
    description: NONPROFIT_MORTGAGE_DESCRIPTION,
    jsonLd: NONPROFIT_MORTGAGE_JSON_LD,
  },

  {
    path: "/tools/combining-assistance-programs",
    title: COMBINING_ASSISTANCE_TITLE,
    description: COMBINING_ASSISTANCE_DESCRIPTION,
    jsonLd: COMBINING_ASSISTANCE_JSON_LD,
  },
  {
    path: "/tools/free-homebuying-readiness-tools",
    title: FREE_READINESS_TOOLS_TITLE,
    description: FREE_READINESS_TOOLS_DESCRIPTION,
    jsonLd: FREE_READINESS_TOOLS_JSON_LD,
  },
  {
    path: "/tools/assistance-funding-availability",
    title: ASSISTANCE_FUNDING_TITLE,
    description: ASSISTANCE_FUNDING_DESCRIPTION,
    jsonLd: ASSISTANCE_FUNDING_JSON_LD,
  },
  {
    path: "/tools/dpa-hidden-strings",
    title: DPA_HIDDEN_STRINGS_TITLE,
    description: DPA_HIDDEN_STRINGS_DESCRIPTION,
    jsonLd: DPA_HIDDEN_STRINGS_JSON_LD,
  },
  {
    path: "/tools/first-responder-home-buying",
    title: FIRST_RESPONDER_TITLE,
    description: FIRST_RESPONDER_DESCRIPTION,
    jsonLd: FIRST_RESPONDER_JSON_LD,
  },
  {
    path: "/tools/mortgage-preapproval-meeting",
    title: MORTGAGE_PREAPPROVAL_TITLE,
    description: MORTGAGE_PREAPPROVAL_DESCRIPTION,
    jsonLd: MORTGAGE_PREAPPROVAL_JSON_LD,
  },
  {
    path: "/tools/closing-disclosure-review",
    title: CLOSING_DISCLOSURE_TITLE,
    description: CLOSING_DISCLOSURE_DESCRIPTION,
    jsonLd: CLOSING_DISCLOSURE_JSON_LD,
  },
  {
    path: "/tools/compare-loan-estimates",
    title: LOAN_ESTIMATE_COMPARE_TITLE,
    description: LOAN_ESTIMATE_COMPARE_DESCRIPTION,
    jsonLd: LOAN_ESTIMATE_COMPARE_JSON_LD,
  },
  {
    path: "/tools/down-payment-assistance-estimate",
    title: "How Much Down Payment Assistance Could You Realistically Get?",
    description:
      "Estimate your down payment assistance in 60 seconds. Enter your state, income, home price, loan type, and occupation to see a realistic DPA range, repayment type, and next step.",
  },
  {
    path: "/lp/ruben",
    title: "Connect With Ruben Nazario — Free NJ Real Estate Guidance",
    description:
      "Work with Ruben Nazario of The Greater Group of KW. Create a free Home Approach account and message him directly — bilingual New Jersey buyer and seller representation, plus free tools and rewards.",
  },
  {
    path: "/lp/hero-stack",
    title: "Hero Stack — Max Out Homebuying Rewards for Community Heroes",
    description:
      "Hero Stack combines federal, state, county, and employer down-payment assistance with Hero Benefits Alliance rewards for military, first responders, educators, healthcare, and public service buyers. Free to run.",
    image: `${SITE_URL}/og-hero-stack.jpg`,
  } as RouteMeta & { image: string },

  {
    path: "/lp/hero-stack/thank-you",
    title: "Claim Your Hero Layer — Home Approach Stack",
    description:
      "Pick how you want to claim your Hero Benefits Alliance layer: a free call with Randy Nemeroff, or enroll directly with Hero Benefits Alliance.",
  },
  {
    path: "/hero-benefits-enroll",
    title: "Enroll With Hero Benefits Alliance — Home Approach",
    description:
      "Enroll directly with Hero Benefits Alliance for homebuying, selling, or refinancing benefits available to military members, first responders, educators, healthcare workers, and other community heroes.",
  },
  {
    path: "/hero-home-signup",
    title: "Free Hero Homebuying Toolkit — Home Approach",
    description:
      "Get your free grant Stack, lender matches, and Homie guide — plus optional Hero Benefits Alliance enrollment for closing savings.",
  },



  {
    path: "/approved-lenders",
    title: "Approved Lenders by State Housing Agency — Find Yours",
    description:
      "Find which lenders participate in your state's housing agency down payment assistance program, how to verify the official list, and what to ask before you apply.",
  },
];



for (const r of EXTRA) {
  const key = normalizePath(r.path);
  if (!REGISTRY.has(key)) REGISTRY.set(key, r);
}

// Partner directory: hub plus one entry per organization in the registry.
REGISTRY.set(normalizePath(PARTNERS_HUB_PATH), {
  path: PARTNERS_HUB_PATH,
  title: PARTNERS_HUB_TITLE,
  description: PARTNERS_HUB_DESCRIPTION,
  jsonLd: PARTNERS_HUB_JSON_LD,
});
for (const partner of PARTNERS) {
  REGISTRY.set(normalizePath(partner.path), {
    path: partner.path,
    title: partner.title,
    description: partner.description,
    jsonLd: buildPartnerJsonLd(partner),
  });
}

/**
 * Private/auth-gated path prefixes. Mirrors PRIVATE_PATTERNS in
 * src/components/RouteMetaGuard.tsx — these emit robots noindex,nofollow in
 * the server HTML too, so a crawler never needs to run JS to learn that.
 */
const PRIVATE_PREFIXES = [
  "/admin",
  "/dashboard",
  "/profile",
  "/rewards",
  "/points-store",
  "/leaderboard",
  "/goals",
  "/my-plan",
  "/onboarding",
  "/auth",
  "/reset-password",
  "/agent-auth",
  "/agent-dashboard",
  "/expert-auth",
  "/expert-dashboard",
  "/employer-dashboard",
  "/employer-onboarding",
  "/employer-report",
  "/wellness-survey",
  "/pulse",
  "/lp/hero-stack/thank-you",

  "/share-progress",
  "/your-match",
  "/lender-match",
  "/readiness-check",
  "/embed",
  "/preview",
  "/decks",
  "/sponsor-deck",
  "/focus-group",
  "/member-followup",
  "/expert-followup",
  "/stack-check",
  "/contests",
  "/checklist",
  "/success-stories",
  "/neighborhood-watchlist",
  "/lifemart/stack-invite",
];

export function normalizePath(pathname: string): string {
  if (!pathname) return "/";
  const clean = pathname.split("?")[0].split("#")[0];
  if (clean === "/") return "/";
  return clean.replace(/\/+$/, "") || "/";
}

export function isPrivatePath(pathname: string): boolean {
  const p = normalizePath(pathname);
  return PRIVATE_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
}

const DEFAULT_TITLE = "Home Approach Stack — Every Down Payment Dollar, One Total";
const DEFAULT_DESCRIPTION =
  "Free homebuying tools, down payment grant stacking, credit prep, and rewards that pay you for making progress toward owning a home.";

/** Resolves the full server-side meta payload for any pathname. */
export function resolveRouteMeta(pathname: string): ResolvedMeta {
  const path = normalizePath(pathname);
  const entry = REGISTRY.get(path);
  const priv = isPrivatePath(path);

  const title = entry?.title ?? DEFAULT_TITLE;
  const description = entry?.description ?? DEFAULT_DESCRIPTION;

  return {
    title,
    description,
    canonical: `${SITE_URL}${path === "/" ? "/" : path}`,
    ogTitle: entry?.ogTitle ?? title,
    ogDescription: entry?.ogDescription ?? description,
    ogType: path === "/" ? "website" : "article",
    image: (entry as RouteMeta & { image?: string })?.image,
    jsonLd: entry?.jsonLd,

    noIndex: priv,
  };
}
